import React, { useEffect, useMemo, useState } from "react";
import FinancialFilter from "../components/filters/FinancialFilter";
import { useApplication } from "../contexts/ApplicationContext";
import { formatToReal } from "../utils/utils";
import axios from "../config/axiosInstance";
import { FINANCIAL } from "../config/apiConfig";
import ReactApexChart from "react-apexcharts";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiTrash,
  FiPlus,
  FiList,
  FiPieChart,
  FiDownload,
} from "react-icons/fi";

const CATEGORY_OPTIONS = [
  { value: "RECEITA", label: "Receita" },
  { value: "DESPESAS_FIXAS", label: "Despesas Fixas" },
  { value: "DESPESAS_VARIAVEIS", label: "Despesas Variáveis" },
  { value: "INVESTIMENTOS", label: "Investimentos" },
  { value: "IMPOSTOS_TAXAS", label: "Impostos e Taxas" },
];

const TYPE_OPTIONS = [
  { value: "ENTRADA", label: "Entrada" },
  { value: "SAIDA", label: "Saída" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const FinancialManagementPage = () => {
  const { userPermission } = useApplication();
  const isPowerUser = String(userPermission || "").includes("POWER_USER");

  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [isFabOpen, setIsFabOpen] = useState(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    type: "ALL",
    category: "ALL",
    dateFrom: "",
    dateTo: "",
  });
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    type: "ENTRADA",
    category: "RECEITA",
    description: "",
    amount: "",
    date: todayISO(),
    notes: "",
    isRecurring: false,
    recurrenceEndDate: "",
  });

  useEffect(() => {
    try {
      if (!isPowerUser) return;
      const loadEntries = async () => {
        setIsLoading(true);
        setLoadError("");
        try {
          const params = { limit: 500, offset: 0 };
          if (filters.dateFrom) params.dateFrom = filters.dateFrom;
          if (filters.dateTo) params.dateTo = filters.dateTo;
          const res = await axios.get(FINANCIAL.LIST, { params });
          const items =
            res?.data?.data?.items || res?.data?.data || res?.data || [];
          setEntries(Array.isArray(items) ? items : []);
        } catch (err) {
          console.error("Erro ao carregar lançamentos financeiros", err);
          setLoadError(
            err?.response?.data?.error ||
              "Não foi possível carregar os lançamentos.",
          );
        } finally {
          setIsLoading(false);
        }
      };

      loadEntries();
    } catch (err) {
      setEntries([]);
      setIsLoading(false);
    }
  }, [isPowerUser, filters.dateFrom, filters.dateTo]);

  const labelByCategory = useMemo(
    () =>
      CATEGORY_OPTIONS.reduce((acc, item) => {
        acc[item.value] = item.label;
        return acc;
      }, {}),
    [],
  );

  const labelByType = useMemo(
    () =>
      TYPE_OPTIONS.reduce((acc, item) => {
        acc[item.value] = item.label;
        return acc;
      }, {}),
    [],
  );

  const totals = useMemo(() => {
    return entries.reduce(
      (acc, entry) => {
        const amount = Number(entry.amount) || 0;
        if (entry.type === "ENTRADA") acc.entradas += amount;
        if (entry.type === "SAIDA") acc.saidas += amount;
        return acc;
      },
      { entradas: 0, saidas: 0 },
    );
  }, [entries]);

  const balance = totals.entradas - totals.saidas;

  const categoryTotals = useMemo(() => {
    return CATEGORY_OPTIONS.map((category) => {
      const total = entries.reduce((acc, entry) => {
        if (entry.category === category.value) {
          return acc + (Number(entry.amount) || 0);
        }
        return acc;
      }, 0);
      return { ...category, total };
    });
  }, [entries]);

  const [chartView, setChartView] = useState("donut");

  const chartLabels = useMemo(
    () => categoryTotals.map((item) => item.label),
    [categoryTotals],
  );

  const chartSeries = useMemo(
    () => categoryTotals.map((item) => Number(item.total) || 0),
    [categoryTotals],
  );

  const chartTotal = useMemo(
    () => chartSeries.reduce((acc, value) => acc + value, 0),
    [chartSeries],
  );

  const entryToDelete = useMemo(
    () => entries.find((entry) => entry.id === confirmDeleteId) || null,
    [confirmDeleteId, entries],
  );

  const donutOptions = useMemo(
    () => ({
      chart: { type: "donut", toolbar: { show: false } },
      labels: chartLabels,
      colors: ["#111827", "#3B82F6", "#10B981", "#F59E0B", "#EF4444"],
      stroke: { width: 2, colors: ["#FFFFFF"] },
      legend: {
        position: "bottom",
        fontSize: "12px",
        fontFamily: "Montserrat, Helvetica, sans-serif",
        labels: { colors: "#6B7280" },
        markers: { radius: 10 },
        itemMargin: { horizontal: 8, vertical: 4 },
      },
      tooltip: {
        y: {
          formatter: (value) => formatToReal(Number(value) || 0),
        },
      },
      dataLabels: { enabled: false },
      plotOptions: {
        pie: {
          donut: {
            size: "62%",
            labels: { show: false },
          },
        },
      },
    }),
    [chartLabels],
  );

  const barOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      colors: ["#111827", "#3B82F6", "#10B981", "#F59E0B", "#EF4444"],
      grid: {
        borderColor: "#E5E7EB",
        strokeDashArray: 4,
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          horizontal: false,
          columnWidth: "50%",
          distributed: true,
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: chartLabels,
        labels: {
          style: { colors: "#6B7280", fontSize: "11px" },
        },
      },
      yaxis: {
        labels: {
          style: { colors: "#6B7280", fontSize: "11px" },
        },
      },
      tooltip: {
        y: {
          formatter: (value) => formatToReal(Number(value) || 0),
        },
      },
    }),
    [chartLabels],
  );

  const filteredEntries = useMemo(() => {
    const searchTerm = filters.search.trim().toLowerCase();
    return entries
      .filter((entry) => {
        if (filters.type !== "ALL" && entry.type !== filters.type) {
          return false;
        }
        if (filters.category !== "ALL" && entry.category !== filters.category) {
          return false;
        }
        if (searchTerm) {
          const haystack = `${entry.description} ${
            entry.notes || ""
          }`.toLowerCase();
          if (!haystack.includes(searchTerm)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (a.date === b.date) return 0;
        return a.date > b.date ? -1 : 1;
      });
  }, [entries, filters]);

  const formatCurrencyInput = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits) return "";
    const number = Number(digits) / 100;
    return number.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const parseCurrencyInput = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits) return 0;
    return Number(digits) / 100;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const description = form.description.trim();
    const amount = parseCurrencyInput(form.amount);

    if (!description) {
      setError("Informe uma descrição para o lançamento.");
      return;
    }

    if (!form.category) {
      setError("Selecione uma categoria.");
      return;
    }

    if (!form.type) {
      setError("Selecione o tipo do lançamento.");
      return;
    }

    if (!form.date) {
      setError("Informe uma data válida.");
      return;
    }

    if (Number.isNaN(amount) || amount <= 0) {
      setError("Informe um valor maior que zero.");
      return;
    }

    if (form.isRecurring && !form.recurrenceEndDate) {
      setError("Informe a data final da recorrência.");
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await axios.post(FINANCIAL.CREATE, {
        type: form.type,
        category: form.category,
        description,
        amount,
        date: form.date,
        notes: form.notes.trim(),
        isRecurring: form.isRecurring,
        recurrenceEndDate: form.isRecurring ? form.recurrenceEndDate : null,
        recurrenceInterval: form.isRecurring ? "monthly" : null,
      });
      const created = res?.data?.data || res?.data;
      if (created) {
        setEntries((prev) => [created, ...prev]);
      }
      setForm((prev) => ({
        ...prev,
        description: "",
        amount: "",
        notes: "",
        isRecurring: false,
        recurrenceEndDate: "",
      }));
      setIsMobileModalOpen(false);
    } catch (err) {
      console.error("Erro ao criar lançamento financeiro", err);
      setError(
        err?.response?.data?.error || "Não foi possível salvar o lançamento.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async (id) => {
    try {
      setIsDeleting(true);
      await axios.delete(FINANCIAL.DELETE(id));
      setEntries((prev) => prev.filter((entry) => entry.id !== id));
    } catch (err) {
      console.error("Erro ao remover lançamento financeiro", err);
      setError(
        err?.response?.data?.error || "Não foi possível remover o lançamento.",
      );
    } finally {
      setIsDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  const openDeleteModal = (id) => setConfirmDeleteId(id);
  const closeDeleteModal = () => setConfirmDeleteId(null);

  const openMobileModal = (type) => {
    setForm((prev) => ({ ...prev, type }));
    setIsMobileModalOpen(true);
    setIsFabOpen(false);
  };

  const closeMobileModal = () => {
    setIsMobileModalOpen(false);
    setIsFabOpen(false);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const params = {};
      if (filters.search) params.q = filters.search;
      if (filters.type !== "ALL") params.type = filters.type;
      if (filters.category !== "ALL") params.category = filters.category;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const res = await axios.get(FINANCIAL.EXPORT, {
        params,
        responseType: "blob",
      });

      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lancamentos_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Erro ao exportar lançamentos", e);
    } finally {
      setIsExporting(false);
    }
  };

  const formatSignedValue = (value) => {
    if (value < 0) {
      return `- ${formatToReal(Math.abs(value))}`;
    }
    return formatToReal(value);
  };

  const formatEntryValue = (entry) => {
    const base = formatToReal(Number(entry.amount) || 0);
    return entry.type === "SAIDA" ? `- ${base}` : base;
  };

  const fabItemClass = (open) =>
    `flex items-center gap-3 rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-lg transition-all duration-200 ${
      open
        ? "opacity-100 translate-y-0 scale-100"
        : "opacity-0 translate-y-2 scale-95 pointer-events-none"
    }`;

  if (!isPowerUser) {
    return (
      <div className="bg-white rounded-md shadow-md p-5">
        <h1 className="text-lg font-semibold text-gray-700">
          Gestão Financeira
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Esta seção é restrita a usuários com permissão POWER_USER.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Filtros no topo */}
      <FinancialFilter
        filters={filters}
        setFilters={setFilters}
        TYPE_OPTIONS={TYPE_OPTIONS}
        CATEGORY_OPTIONS={CATEGORY_OPTIONS}
      />

      <div className="grid gap-2 grid-cols-2 lg:grid-cols-4">
        {/* Saldo */}
        <div className="col-span-2 rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
          <div
            className={`h-1 w-full ${balance >= 0 ? "bg-emerald-400" : "bg-red-400"}`}
          />
          <div className="p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-[9px] font-bold text-gray-400 tracking-widest uppercase">
                  Saldo Atual
                </p>
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${balance >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}
                >
                  {balance >= 0 ? "↑ positivo" : "↓ negativo"}
                </span>
              </div>
              <p
                className={`text-2xl font-bold tracking-tight ${balance >= 0 ? "text-emerald-600" : "text-red-500"}`}
              >
                {formatSignedValue(balance)}
              </p>
            </div>
            {totals.entradas + totals.saidas > 0 && (
              <div className="shrink-0 w-28">
                <div className="flex justify-between text-[9px] text-gray-400 mb-1">
                  <span className="text-emerald-500 font-medium">
                    ↑{" "}
                    {Math.round(
                      (totals.entradas / (totals.entradas + totals.saidas)) *
                        100,
                    )}
                    %
                  </span>
                  <span className="text-red-400 font-medium">
                    {Math.round(
                      (totals.saidas / (totals.entradas + totals.saidas)) * 100,
                    )}
                    % ↓
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full overflow-hidden bg-red-100">
                  <div
                    className="h-full rounded-full bg-emerald-400 transition-all duration-500"
                    style={{
                      width: `${Math.min(100, (totals.entradas / (totals.entradas + totals.saidas)) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Entradas */}
        <div className="rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
          <div className="h-1 w-full bg-emerald-400" />
          <div className="p-3">
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50 text-emerald-500 text-xs font-bold">
                ↑
              </span>
              <p className="text-[9px] font-bold text-gray-400 tracking-widest uppercase">
                Entradas
              </p>
            </div>
            <p className="text-lg font-bold text-emerald-600 leading-none">
              {formatToReal(totals.entradas)}
            </p>
            <p className="text-[9px] text-gray-400 mt-1.5">
              Receitas confirmadas
            </p>
          </div>
        </div>

        {/* Saídas */}
        <div className="rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
          <div className="h-1 w-full bg-red-400" />
          <div className="p-3">
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-50 text-red-400 text-xs font-bold">
                ↓
              </span>
              <p className="text-[9px] font-bold text-gray-400 tracking-widest uppercase">
                Saídas
              </p>
            </div>
            <p className="text-lg font-bold text-red-500 leading-none">
              {formatToReal(totals.saidas)}
            </p>
            <p className="text-[9px] text-gray-400 mt-1.5">
              Despesas registradas
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="hidden md:block bg-white rounded-md shadow-md p-5">
          <div className="flex items-center gap-2.5 pb-3 mb-1 border-b border-gray-100">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-900 text-white shrink-0">
              <FiPlus size={13} />
            </span>
            <h2 className="text-sm font-semibold text-gray-800">
              Novo Lançamento
            </h2>
          </div>
          <form onSubmit={handleSubmit} className="mt-3 flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Tipo</label>
              <select
                className="border border-gray-200 rounded-md px-3 py-2 text-sm"
                value={form.type}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    type: event.target.value,
                  }))
                }
              >
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Categoria</label>
              <select
                className="border border-gray-200 rounded-md px-3 py-2 text-sm"
                value={form.category}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    category: event.target.value,
                  }))
                }
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-gray-400">
                Impostos e taxas ficam separados, mas normalmente entram como
                saída.
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Descrição</label>
              <input
                className="border border-gray-200 rounded-md px-3 py-2 text-sm"
                value={form.description}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
                placeholder="Ex: Mensalidade do sistema"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500">Valor</label>
                <input
                  className="border border-gray-200 rounded-md px-3 py-2 text-sm"
                  type="text"
                  inputMode="decimal"
                  value={form.amount}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      amount: formatCurrencyInput(event.target.value),
                    }))
                  }
                  placeholder="0,00"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500">Data</label>
                <input
                  className="border border-gray-200 rounded-md px-3 py-2 text-sm"
                  type="date"
                  value={form.date}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, date: event.target.value }))
                  }
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Observações</label>
              <textarea
                className="border border-gray-200 rounded-md px-3 py-2 text-sm min-h-[80px]"
                value={form.notes}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    notes: event.target.value,
                  }))
                }
                placeholder="Opcional"
              />
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-medium text-gray-700">
                    Recorrente mensal
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Repete todo mês automaticamente
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      isRecurring: !prev.isRecurring,
                      recurrenceEndDate: !prev.isRecurring
                        ? prev.recurrenceEndDate
                        : "",
                    }))
                  }
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                    form.isRecurring ? "bg-gray-900" : "bg-gray-300"
                  }`}
                >
                  <span
                    className="inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-200"
                    style={{
                      transform: form.isRecurring
                        ? "translateX(18px)"
                        : "translateX(2px)",
                    }}
                  />
                </button>
              </div>
              {form.isRecurring && (
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                    Data fim da recorrência
                  </label>
                  <input
                    type="date"
                    className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
                    value={form.recurrenceEndDate}
                    min={form.date}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        recurrenceEndDate: e.target.value,
                      }))
                    }
                  />
                </div>
              )}
            </div>

            {error && (
              <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-md bg-black text-white px-3 py-2 text-sm hover:bg-gray-800 transition disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Salvando..." : "Adicionar lançamento"}
            </button>
          </form>
        </div>

        <div className="lg:col-span-2 flex flex-col gap-4">
          <div className="bg-white rounded-md shadow-md p-5">
            <div className="flex items-center justify-between gap-2 pb-3 mb-1 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50 text-blue-500 shrink-0">
                  <FiList size={13} />
                </span>
                <div>
                  <h2 className="text-sm font-semibold text-gray-800">
                    Lançamentos
                  </h2>
                  <p className="text-[11px] text-gray-400 leading-tight">
                    Entradas e saídas registradas.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleExport}
                disabled={isExporting}
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition hover:bg-gray-50 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiDownload size={12} />
                {isExporting ? "Exportando..." : "Exportar Excel"}
              </button>
            </div>

            {loadError && (
              <p className="mt-3 text-xs text-red-500 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                {loadError}
              </p>
            )}

            <div
              className="mt-4 overflow-x-auto hidden md:block overflow-y-auto pr-2"
              style={{ maxHeight: "300px" }}
            >
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4 font-medium">Data</th>
                    <th className="py-2 pr-4 font-medium">Tipo</th>
                    <th className="py-2 pr-4 font-medium">Categoria</th>
                    <th className="py-2 pr-4 font-medium">Descrição</th>
                    <th className="py-2 pr-4 font-medium text-right">Valor</th>
                    <th className="py-2 font-medium text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-6 text-center text-gray-400"
                      >
                        Carregando lançamentos...
                      </td>
                    </tr>
                  )}
                  {!isLoading && filteredEntries.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-6 text-center text-gray-400"
                      >
                        Nenhum lançamento encontrado.
                      </td>
                    </tr>
                  )}
                  {filteredEntries.map((entry) => (
                    <tr
                      key={entry.id}
                      className="border-b border-gray-100 last:border-none"
                    >
                      <td className="py-3 pr-4 text-gray-600">
                        {new Date(`${entry.date}T00:00:00`).toLocaleDateString(
                          "pt-BR",
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            entry.type === "ENTRADA"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-red-50 text-red-600"
                          }`}
                        >
                          {labelByType[entry.type] || entry.type}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-gray-600">
                        {labelByCategory[entry.category] || entry.category}
                      </td>
                      <td className="py-3 pr-4 text-gray-700">
                        <div className="font-medium">{entry.description}</div>
                        {entry.notes && (
                          <div className="text-xs text-gray-400 mt-1">
                            {entry.notes}
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-right font-medium">
                        {formatEntryValue(entry)}
                      </td>
                      <td className="py-3 text-right">
                        <button
                          className="text-sm text-red-500 hover:text-red-700"
                          onClick={() => openDeleteModal(entry.id)}
                        >
                          <FiTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 space-y-3 md:hidden">
              {isLoading && (
                <div className="border border-dashed border-gray-200 rounded-md px-4 py-6 text-center text-sm text-gray-400">
                  Carregando lançamentos...
                </div>
              )}
              {!isLoading && filteredEntries.length === 0 && (
                <div className="border border-dashed border-gray-200 rounded-md px-4 py-6 text-center text-sm text-gray-400">
                  Nenhum lançamento encontrado.
                </div>
              )}
              {!isLoading &&
                filteredEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs text-gray-500">
                          {new Date(
                            `${entry.date}T00:00:00`,
                          ).toLocaleDateString("pt-BR")}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-gray-700">
                          {entry.description}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          entry.type === "ENTRADA"
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-red-50 text-red-600"
                        }`}
                      >
                        {labelByType[entry.type] || entry.type}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        {labelByCategory[entry.category] || entry.category}
                      </p>
                      <p className="text-sm font-semibold text-gray-700">
                        {formatEntryValue(entry)}
                      </p>
                    </div>
                    {entry.notes && (
                      <p className="mt-2 text-xs text-gray-400">
                        {entry.notes}
                      </p>
                    )}
                    <div className="mt-3 flex justify-end">
                      <button
                        className="text-xs text-red-500 hover:text-red-700"
                        onClick={() => openDeleteModal(entry.id)}
                      >
                        <FiTrash />
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="bg-white rounded-md shadow-md p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pb-3 mb-1 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-50 text-purple-500 shrink-0">
                  <FiPieChart size={13} />
                </span>
                <div>
                  <h2 className="text-sm font-semibold text-gray-800">
                    Resumo por Categoria
                  </h2>
                  <p className="text-[11px] text-gray-400 leading-tight">
                    Escolha o formato de visualização.
                  </p>
                </div>
              </div>
              <div className="flex items-center rounded-md border border-gray-200 bg-white p-1 text-xs w-fit">
                <button
                  type="button"
                  onClick={() => setChartView("donut")}
                  className={`px-3 py-1 rounded-md transition ${
                    chartView === "donut"
                      ? "bg-gray-900 text-white"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Padrão
                </button>
                <button
                  type="button"
                  onClick={() => setChartView("bar")}
                  className={`px-3 py-1 rounded-md transition ${
                    chartView === "bar"
                      ? "bg-gray-900 text-white"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Barra
                </button>
              </div>
            </div>
            <div className="mt-4">
              {chartSeries.every((value) => value === 0) ? (
                <div className="border border-dashed border-gray-200 rounded-md px-4 py-8 text-center text-sm text-gray-400">
                  Sem dados suficientes para exibir o gráfico.
                </div>
              ) : (
                <div className="flex flex-col items-center w-full">
                  {chartView === "donut" ? (
                    <div className="w-full">
                      <ReactApexChart
                        key="chart-donut"
                        type="donut"
                        height={320}
                        width="100%"
                        options={donutOptions}
                        series={chartSeries}
                      />
                    </div>
                  ) : (
                    <div className="w-full">
                      <ReactApexChart
                        key="chart-bar"
                        type="bar"
                        height={360}
                        width="100%"
                        options={barOptions}
                        series={[{ name: "Total", data: chartSeries }]}
                      />
                    </div>
                  )}
                  <div className="mt-2 text-center">
                    <p className="text-xs font-medium text-gray-500">Total</p>
                    <p className="text-lg font-semibold text-gray-700">
                      {formatToReal(chartTotal)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMobileModalOpen && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-end justify-center md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute inset-0 bg-black"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              exit={{ opacity: 0 }}
              onClick={closeMobileModal}
            />
            <motion.div
              className="relative w-full max-w-md rounded-t-2xl bg-white px-5 pb-6 pt-3 shadow-2xl z-[61]"
              initial={{ y: 48, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 48, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mx-auto h-1.5 w-10 rounded-full bg-gray-200" />
              <div className="mt-3 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-800">
                    Novo Lançamento
                  </h3>
                  <p className="text-xs text-gray-500">
                    Registre uma entrada ou despesa
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeMobileModal}
                  className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  Fechar
                </button>
              </div>

              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({ ...prev, type: "ENTRADA" }))
                  }
                  className={`flex-1 rounded-full border px-3 py-2 text-xs font-medium transition ${
                    form.type === "ENTRADA"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-gray-200 text-gray-500"
                  }`}
                >
                  Entrada
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({ ...prev, type: "SAIDA" }))
                  }
                  className={`flex-1 rounded-full border px-3 py-2 text-xs font-medium transition ${
                    form.type === "SAIDA"
                      ? "border-rose-200 bg-rose-50 text-rose-700"
                      : "border-gray-200 text-gray-500"
                  }`}
                >
                  Despesa
                </button>
              </div>

              <form
                onSubmit={handleSubmit}
                className="mt-4 flex flex-col gap-3"
              >
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">Categoria</label>
                  <select
                    className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                    value={form.category}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        category: event.target.value,
                      }))
                    }
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">Descrição</label>
                  <input
                    className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                    value={form.description}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        description: event.target.value,
                      }))
                    }
                    placeholder="Ex: Mensalidade do sistema"
                  />
                </div>

                <div className="grid grid-cols-1 gap-2">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500">Valor</label>
                    <input
                      className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                      type="text"
                      inputMode="decimal"
                      value={form.amount}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          amount: formatCurrencyInput(event.target.value),
                        }))
                      }
                      placeholder="0,00"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500">Data</label>
                    <input
                      className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                      type="date"
                      value={form.date}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          date: event.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">Observações</label>
                  <textarea
                    className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm min-h-[90px]"
                    value={form.notes}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        notes: event.target.value,
                      }))
                    }
                    placeholder="Opcional"
                  />
                </div>

                {error && (
                  <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                    {error}
                  </p>
                )}

                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeMobileModal}
                    className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="rounded-lg bg-black text-white px-4 py-2 text-sm hover:bg-gray-800 transition disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Salvando..." : "Salvar"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmDeleteId && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-center justify-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute inset-0 bg-black"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={closeDeleteModal}
            />
            <motion.div
              className="relative w-full max-w-sm rounded-xl bg-white p-5 shadow-xl"
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 16, opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={(event) => event.stopPropagation()}
            >
              <h3 className="text-sm font-semibold text-gray-800">
                Confirmar exclusão
              </h3>
              <p className="mt-2 text-xs text-gray-500">
                Deseja remover este lançamento?
              </p>
              {entryToDelete && (
                <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                  <p className="text-xs text-gray-500">
                    {new Date(
                      `${entryToDelete.date}T00:00:00`,
                    ).toLocaleDateString("pt-BR")}
                  </p>
                  <p className="text-sm font-semibold text-gray-700">
                    {entryToDelete.description}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatEntryValue(entryToDelete)}
                  </p>
                </div>
              )}
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  className="rounded-md border border-gray-200 px-3 py-2 text-xs text-gray-600"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => handleRemove(confirmDeleteId)}
                  disabled={isDeleting}
                  className="rounded-md bg-red-600 px-3 py-2 text-xs text-white hover:bg-red-700 disabled:opacity-70"
                >
                  {isDeleting ? "Excluindo..." : "Excluir"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="md:hidden">
        <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2">
          <button
            type="button"
            onClick={() => openMobileModal("ENTRADA")}
            className={fabItemClass(isFabOpen)}
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 text-xs font-bold">
              +
            </span>
            <div className="text-left">
              <p className="text-sm font-semibold">Entrada</p>
              <p className="text-[11px] text-gray-400">Receber valor</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => openMobileModal("SAIDA")}
            className={fabItemClass(isFabOpen)}
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-rose-50 text-rose-600 text-xs font-bold">
              -
            </span>
            <div className="text-left">
              <p className="text-sm font-semibold">Despesa</p>
              <p className="text-[11px] text-gray-400">Registrar saída</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setIsFabOpen((prev) => !prev)}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-black text-white text-2xl shadow-lg transition active:scale-95"
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinancialManagementPage;
